import { IVetModel } from "@fuse/models";

function text(value: unknown): string {
	return typeof value === "string" ? value.trim() : "";
}

function record(value: unknown): Record<string, any> {
	return value !== null && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function placesData(json: string): Record<string, any> {
	try { return record(JSON.parse(json)); } catch { return {}; }
}

function coordinate(value: unknown, limit: number): number | null {
	const number = typeof value === "number" ? value : text(value) ? Number(value) : NaN;
	return Number.isFinite(number) && Math.abs(number) <= limit ? number : null;
}

function photoUrl(logo: string, mapKey: string): string {
	const reference = logo.match(/[?&]1s([^&]+)/)?.[1];
	return reference && mapKey
		? `https://maps.googleapis.com/maps/api/place/photo?maxheight=300&maxwidth=400&photo_reference=${encodeURIComponent(reference)}&key=${encodeURIComponent(mapKey)}`
		: logo;
}

/** Stored contact details remain usable when optional Places data is absent or incomplete. */
export function vetProfileDetails(vet: IVetModel, mapKey = "") {
	const places = placesData(vet.placesJson);
	const location = record(record(places.geometry).location);
	const latitude = coordinate(location.lat, 90);
	const longitude = coordinate(location.lng, 180);
	const rating = coordinate(places.rating, 5);
	const weekdayText = record(places.opening_hours).weekday_text;
	const openingHours = (Array.isArray(weekdayText) ? weekdayText : [])
		.filter((hours): hours is string => typeof hours === "string" && !!hours.trim())
		.map((hours) => {
			const separator = hours.indexOf(":");
			return separator < 0 ? { day: hours.trim(), time: "" } : {
				day: hours.slice(0, separator).trim(), time: hours.slice(separator + 1).trim(),
			};
		});
	const stored = vet as IVetModel & { website?: string; reviewsCount?: number };
	const reviewsCount = [places.user_ratings_total, stored.reviewsCount, vet.reviews]
		.find(value => typeof value === "number" && Number.isFinite(value) && value >= 0) ?? 0;
	return {
		detail: {
			...vet,
			name: text(vet.name) || "Veterinarian",
			address1: text(places.formatted_address) || text(vet.address1),
			phoneNumber: text(places.formatted_phone_number) || text(vet.phoneNumber),
			website: text(places.website) || text(stored.website) || text(vet.url),
			rating: rating !== null && rating >= 0 ? rating : Math.min(5, Math.max(0, Number(vet.rating) || 0)),
			reviewsCount,
			imageUrl: photoUrl(text(vet.logo), mapKey) || text(vet.imageUrl) || "/assets/img/logo.png",
			practiceType: Array.isArray(places.types) && places.types.includes("veterinary_care")
				? "Veterinary Care" : text(vet.practiceType),
		},
		coordinates: latitude !== null && longitude !== null ? { latitude, longitude } : null,
		openingHours,
	};
}
